"use client";

// WO-MEMBER-STATE-PROPAGATION-001 · T2 — SAY, IN THE HEADER, THAT THE USER IS SIGNED IN.
//
// FOUNDER: "After sign-in the header is identical to signed-out, so users re-click sign-in believing
// it failed."
//
// MEASURED, and the header is not uniformly guilty — which is why this is a shared chip rather than
// three separate edits:
//   SiteHeader.tsx        9 session refs — but the signed-in identity is rendered INSIDE the
//                         hamburger/profile dropdown (`clickProfile`), so the bar AT REST is byte
//                         identical signed-in and signed-out. The founder is right about what he saw;
//                         the state existed, it was just behind a click.
//   SiteHeaderContent.tsx 0 session refs
//   SiteHeaderHub.tsx     0 session refs
//   ContentPage header    0 session refs
// Three of the four header variants had no signed-in state at all, so fixing only the one that has a
// dropdown would leave most of the site looking signed-out.
//
// AT REST IS THE WHOLE REQUIREMENT. A state you have to open a menu to see does not stop someone
// re-clicking sign-in, so this renders in the bar itself, always, with the account and a sign-out
// control both reachable without opening anything.
//
// ⚠️ `mounted` IS LOAD-BEARING, NOT DEFENSIVE NOISE. The session lives in a zustand/persist store
// hydrated from localStorage, so the server render and the first client render disagree by
// construction. Rendering the signed-in branch before mount is a hydration mismatch, and React
// silently discards the mismatched subtree — the exact failure mode where the fix "works" in
// isolation and does nothing on the real page. SiteHeader.tsx already carries this guard for
// `ticketsHref` and records the same reason.
//
// Renders NOTHING when signed out: the signed-out header keeps its existing sign-in affordance, and
// adding a second one would be the noise this WO is trying to remove.

import { useEffect, useState } from "react";
import { isSignedIn, useCartStore } from "@/lib/cart-store";

export default function AccountChip({ compact = false }: { compact?: boolean }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const session = useCartStore((s) => s.session);
  const signOut = useCartStore((s) => s.signOut);
  const signedIn = mounted && isSignedIn(session); // bearer OR server-verified cookie-backed SSO session

  if (!signedIn) return null;

  const email = session.email || "Signed in";
  // The part of an address a person recognises. Full address stays in `title` for the ambiguous case.
  const short = email.includes("@") ? email.split("@")[0]! : email;
  const initial = short.slice(0, 1).toUpperCase();

  return (
    <span
      data-account-chip="signed-in"
      data-account-email={email}
      title={email}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginLeft: "auto",
        padding: "4px 6px 4px 4px",
        border: "1px solid var(--sg-glacier-100, #D5E4E8)",
        borderRadius: 999,
        background: "var(--sg-color-summit-white, #fff)",
        fontFamily: "var(--sg-font-body, Roboto, system-ui, sans-serif)",
        fontSize: 13,
        lineHeight: 1.2,
        maxWidth: "100%",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: "var(--sg-color-avalanche-orange, #FC500E)",
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 12,
          flex: "0 0 auto",
        }}
      >
        {initial}
      </span>
      {/* The name is hidden on the narrowest viewports, where the bar has no room for it — but the
          avatar and the sign-out control stay, so the signed-in state is never invisible on mobile.
          That is the requirement ("all viewports"), and it is not the same as rendering everything. */}
      {!compact && (
        <span
          data-account-name
          style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {short}
        </span>
      )}
      <button
        type="button"
        data-account-signout
        onClick={() => signOut()}
        style={{
          border: "none",
          background: "transparent",
          padding: "2px 6px",
          cursor: "pointer",
          color: "var(--sg-color-avalanche-orange, #FC500E)",
          fontWeight: 700,
          fontSize: 12,
          whiteSpace: "nowrap",
        }}
      >
        Sign out
      </button>
    </span>
  );
}
